import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { requestManufacturers } from 'requests/manufacturers'
import { requestProductCategories } from 'requests/product-categories'
import { requestProductGroups } from 'requests/product-groups'
import { PRODUCT_INFO } from 'constants/app-paths'
import {
  requestProductsList,
  requestProductCreate,
  requestProductsListDonwload,
} from 'requests/products'
import { newProduct } from 'constants/objects/product'
import { PRODUCTS_SUBMENU } from 'constants/menu/submenus'
import { ListPage } from 'templates/ListPage'
import { Button } from 'ui-components/Button'
import { Form } from 'components/Product/Form'
import { SideMenu } from 'components/SideMenu'
import { ListSkeleton } from 'components/ListSkeleton'
import { Product } from './components/Product'
import { SearchForm } from './components/SearchForm'
import { Hidden, ListOptions } from './styles'

export default function ProductsList() {
  const [products, setProducts] = useState([])
  const [pagination, setPagination] = useState()
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [responseError, setResponseError] = useState({})
  const [manufacturers, setManufacturers] = useState([])
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])
  const [filters, setFilters] = useState()
  const history = useHistory()

  const downloadRef = useRef()

  const getManufacturers = useCallback(async () => {
    const response = await requestManufacturers()
    if (response.error) return setManufacturers(response)

    const responseOptions = response.data.map((item) => ({
      value: item.id.toString(), label: item.attributes.name,
    }))

    return setManufacturers(responseOptions)
  }, [])

  const getCategories = useCallback(async () => {
    const response = await requestProductCategories()
    if (response.error) return setCategories(response)

    const options = response.data.map((item) => ({
      value: item.id.toString(), label: item.attributes.name,
    }))

    return setCategories(options)
  }, [])

  const getGroups = useCallback(async () => {
    const response = await requestProductGroups()
    if (response.error) return setGroups(response)

    const options = response.data.map((item) => ({
      value: item.id.toString(), label: item.attributes.name,
    }))

    return setGroups(options)
  }, [])

  const getProducts = useCallback(async (page = 1, params = {}) => {
    setLoading(true)
    const response = await requestProductsList(page, params)

    if (response.error) return setResponseError(response)

    setProducts(response.data)
    setPagination(response.meta?.pagination)
    return setLoading(false)
  }, [])

  useEffect(() => {
    getManufacturers()
    getGroups()
    getCategories()
  }, [getManufacturers, getCategories, getGroups])

  useEffect(() => {
    const query = new URLSearchParams(window.location.search)
    const page = query.get('page')
    const params = {}
    query.forEach((val, key) => {
      if (val && val !== 'undefined' && key !== 'page') {
        params[key] = val
      }
    })

    setFilters(params)
    getProducts(page, params)
  }, [getProducts])

  const redirectToInfo = (id) => {
    const path = generatePath(PRODUCT_INFO, { id })
    history.push(path)
  }

  const onClickProduct = (id) => {
    redirectToInfo(id)
  }

  const onClickPagination = (page) => {
    const params = new URLSearchParams(filters)
    params.append('page', page)
    history.push({ search: params.toString() })
    getProducts(page, filters)
  }

  const onToggleForm = (show = false) => {
    setShowForm(show)
  }

  const onChangeFilters = (name, value) => {
    const changeFilters = { ...filters }
    if (!value) {
      delete changeFilters[name]
    } else {
      changeFilters[name] = value
    }

    setFilters(changeFilters)
  }

  const onSearch = () => {
    const query = new URLSearchParams(filters)
    history.push({ search: query.toString() })
    getProducts(1, filters)
  }

  const onCreate = async (product) => {
    const response = await requestProductCreate(product)
    if (response.error) return setResponseError(response)

    return redirectToInfo(response.data.id)
  }

  const downloadList = async () => {
    const file = await requestProductsListDonwload(filters)
    const url = URL.createObjectURL(new Blob([file]))

    downloadRef.current.href = url;
    downloadRef.current.setAttribute(
      'download',
      `produtos-${new Date().toISOString()}.csv`,
    );

    downloadRef.current.click();
  }

  const renderProduct = (product) => (
    <Product
      key={`product-${product.id}`}
      product={product}
      onClick={onClickProduct}
    />
  )

  const renderLoading = () => (
    <ListSkeleton />
  )

  const renderList = () => {
    if (loading) return renderLoading()

    return products.map(renderProduct)
  }

  const renderForm = () => (
    <Form
      product={newProduct}
      onCancel={() => onToggleForm(false)}
      onSave={onCreate}
      responseError={responseError}
      isNewProduct
    />
  )

  const renderSeachForm = () => (
    <SearchForm
      manufacturers={manufacturers}
      categories={categories}
      groups={groups}
      onSearch={onSearch}
      filters={filters}
      onChange={onChangeFilters}
    />
  )

  return (
    <ListPage
      renderForm={renderForm}
      renderMenu={() => <SideMenu submenu={PRODUCTS_SUBMENU} />}
      listTitle="Produtos"
      onClickPagination={onClickPagination}
      pagination={pagination}
      loading={loading}
      showForm={showForm}
      onToggleForm={onToggleForm}
      showAddButton
      renderSearchForm={renderSeachForm}
      openSearchForm
    >
      { renderList() }

      <ListOptions>
        <Button onClick={downloadList}>
          Download CSV
        </Button>
      </ListOptions>

      <Hidden>
        <a href={window.location.toString()} ref={downloadRef}>
          Download File
        </a>
      </Hidden>
    </ListPage>
  )
}
